import { useMemo } from 'react';

import type { UUID } from '@aline/common';
import { link, routes } from '@aline/routes';
import { useMatch } from 'react-router-dom';
import type { CommentThreadComment } from 'src/components/comments';
import { Apollo } from 'src/gql/generated';
import type { WorkflowFileSubset } from 'src/hooks';
import type { LabelSubset, UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';

export interface WorkflowDetailMeta extends Pick<
  Apollo.Workflow,
  | 'id'
  | '__typename'
  | 'aiCancelledAt'
  | 'aiFailedAt'
  | 'aiFinishedAt'
  | 'aiStartedAt'
  | 'crmIntegrations'
  | 'name'
  | 'priority'
  | 'publishedAt'
  | 'skipTriage'
  | 'startedAt'
  | 'variant'
  | 'state'
> {
  column: Pick<Apollo.Column, 'id' | 'name' | 'category' | 'color' | 'sortKey'> & {
    board: Pick<Apollo.Board, 'id' | 'name' | 'icon' | 'color'>;
  };
  comments: CommentThreadComment[];
  doneColumn: Pick<Apollo.Column, 'id' | 'name' | 'category' | 'color' | 'sortKey'>;
  files: WorkflowFileSubset[];
  labels: LabelSubset[];
  owner: UserWithOrg;
  permissions: WorkflowPermissionSubset[];
  phases: Array<
    Pick<Apollo.Phase, 'completedAt' | 'id' | 'name' | 'order' | 'startedAt'> & {
      column: Pick<Apollo.Column, 'id'>;
      __typename?: string;
    }
  >;
}

export interface WorkflowDetailLinks {
  baseUrl: () => string;
  collaborators: () => string;
  comments: () => string;
  documents: () => string;
  isTemplateRoute: boolean;
  phaseSettings: (phaseId: UUID) => string;
  settings: () => string;
  task: (taskId: UUID) => string;
  workflow: WorkflowDetailMeta | null;
}

/**
 * Returns route-aware links and workflow meta for the current workflow detail page.
 * Works for both /workflows/:workflowId and /templates/:workflowId.
 */
export function useWorkflowDetailLinks(workflowId: UUID): WorkflowDetailLinks {
  const templateMatch = useMatch({ end: false, path: routes.TemplateSelectedTemplateIndex });
  const { data } = Apollo.useGetWorkflowHeaderQuery({ variables: { workflowId } });
  const isTemplateRoute = Boolean(templateMatch);

  return useMemo(() => {
    return {
      baseUrl: () =>
        isTemplateRoute
          ? link.TemplateSelectedTemplateIndex({ workflowId })
          : link.WorkflowDetail({ workflowId }),
      collaborators: () =>
        isTemplateRoute
          ? link.TemplateSelectedTemplateTeam({ workflowId })
          : link.WorkflowDetailTabCollaborators({ workflowId }),
      comments: () =>
        isTemplateRoute
          ? link.TemplateSelectedTemplateComments({ workflowId })
          : link.WorkflowDetailTabComments({ workflowId }),
      documents: () =>
        isTemplateRoute
          ? link.TemplateSelectedTemplateDocuments({ workflowId })
          : link.WorkflowDetailTabDocuments({ workflowId }),
      isTemplateRoute,
      phaseSettings: (phaseId: UUID) =>
        link.WorkflowDetailTabPhaseSettings({ phaseId, workflowId }),
      settings: () => link.WorkflowDetailTabOptions({ workflowId }),
      task: (taskId: UUID) => link.WorkflowDetailTabTask({ taskId, workflowId }),
      workflow: data?.workflow ?? null,
    };
  }, [data?.workflow, isTemplateRoute, workflowId]);
}
